"use client";
import { summaryPdfProductsOverlap, isSummaryPdfBundle } from "@/lib/summary-pdf-sales";

import { useUser } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "@sentry/nextjs";
import { Check, Gift, ShoppingBag } from "lucide-react";
import { usePaymentPolling } from "@/hooks/usePaymentPolling";
import PaymentPollingOverlay from "@/components/PaymentPollingOverlay";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { generateOrderId } from "@/lib/pass-utils";
import { getPaymentMethods, getPaymentProvider, type PaymentMethod } from "@/lib/payment-provider";
import TossPaymentWidget, { type TossPaymentWidgetHandle } from "@/components/TossPaymentWidget";
import { isNicepayPaymentMethod } from "@/lib/nicepay-payment";
import { getNicepayGoodsName } from "@/lib/nicepay-goods-name";
import {
  nicepayVbankOptions,
} from "@/lib/nicepay-payment";

interface PaymentSectionProps {
  price: number;
  orderIdPrefix: string;
  nicepayClientId: string;
  productId: string;
}

// Window 객체에 nicepay 추가
declare global {
  interface Window {
    AUTHNICE: {
      requestPay: (config: {
        clientId: string;
        method: string;
        orderId: string;
        amount: number;
        goodsName: string;
        returnUrl: string;
        buyerName?: string;
        buyerEmail?: string;
        vbankHolder?: string;
        vbankValidHours?: number;
        fnError: (result: { msg: string; errorMsg: string }) => void;
      }) => void;
    };
  }
}

export default function PaymentSection({
  price,
  orderIdPrefix,
  nicepayClientId,
  productId,
}: PaymentSectionProps) {
  const { isSignedIn, isLoaded, user } = useUser();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | "">("");
  const isToss = getPaymentProvider() === "toss";
  const tossWidget = useRef<TossPaymentWidgetHandle>(null);
  const [tossReady, setTossReady] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  // JCG-3580: 이 PDF가 "보유 이용권에 포함"인지는 회차 무관 상수가 아니라 실제 무료 지급
  // 주문(amount=0, status='paid')으로 판정한다. PDF 다운로드 권한도 이 주문이 SSOT다.
  const [hasPassIncludedPdfOrder, setHasPassIncludedPdfOrder] = useState(false);
  const [isCheckingPurchase, setIsCheckingPurchase] = useState(true);
  // JCG-3580: 구매 이력 조회가 실패하면 "이미 보유했는지"를 알 수 없다. 이 상태에서 결제를
  // 열어두면 이용권에 포함되거나 이미 구매한 PDF를 중복 결제할 수 있으므로 결제를 막는다.
  const [purchaseCheckFailed, setPurchaseCheckFailed] = useState(false);
  // 페이지 로드 시 sessionStorage에서 결제 대기 중인 주문 복구
  // lazy useState init으로 mount 시점에 1회 sessionStorage 읽음 (set-state-in-effect 회피, JCG-2029)
  const [pollingOrderId, setPollingOrderId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem("pendingPaymentOrderId");
  });
  const goodsName = getNicepayGoodsName(productId);

  const handlePollingSuccess = useCallback(() => {
    sessionStorage.removeItem('pendingPaymentOrderId');
    setPollingOrderId(null);
    setIsLoading(false);
  }, []);

  const handlePollingError = useCallback((error: string) => {
    sessionStorage.removeItem('pendingPaymentOrderId');
    setPollingOrderId(null);
    setIsLoading(false);
    alert(error);
  }, []);

  usePaymentPolling({
    orderId: pollingOrderId,
    enabled: !!pollingOrderId,
    onSuccess: handlePollingSuccess,
    onError: handlePollingError,
  });

  // visibilitychange: 외부 앱에서 돌아왔을 때 즉시 결제 상태 확인
  useEffect(() => {
    if (!pollingOrderId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetch(`/api/check-order-status?orderId=${encodeURIComponent(pollingOrderId)}`)
          .catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pollingOrderId]);

  // 이용권 정보 확인
  const { passType, isActive: isActivePass } = useEffectivePass();

  // Complete 또는 Intelligence 이용권에 포함된 PDF인지 확인.
  // JCG-3580: 회차 무관 목록(COMPLETE_PASS_INCLUDED_PDFS)으로 판정하면 26년 3회 Complete
  // 보유자에게 26년 1·2회 PDF까지 "무료 포함"으로 잘못 안내되어, 받을 수 없는 PDF를
  // 다운로드하라고 안내하게 된다. 실제 무료 지급 주문 유무로 판정한다.
  const isIncludedInPass =
    !isSummaryPdfBundle(productId) &&
    isActivePass &&
    (passType === 'complete' || passType === 'intelligence') &&
    hasPassIncludedPdfOrder;

  // 구매 이력 확인
  useEffect(() => {
    const checkPurchaseHistory = async () => {
      if (!isSignedIn) {
        setIsCheckingPurchase(false);
        return;
      }

      setPurchaseCheckFailed(false);

      try {
        // JCG-1334: 결제 직후 stale 응답 방지를 위해 브라우저 캐시 우회
        // JCG-2074: 이메일을 쿼리로 넘기지 않고 서버가 Clerk 세션으로 본인 식별
        const response = await fetch("/api/purchases", { cache: "no-store" });

        if (!response.ok) {
          setPurchaseCheckFailed(true);
        } else {
          const data = await response.json();
          const purchases = data.purchases || [];
          // 현재 상품을 이미 구매했는지 확인
          const paidOrdersForProduct = purchases.filter(
            (purchase: { productId: string; status: string }) =>
              (purchase.productId === productId || summaryPdfProductsOverlap(productId, purchase.productId)) && purchase.status === 'paid'
          );
          setHasPurchased(paidOrdersForProduct.length > 0);
          // JCG-3580: 이용권 무료 제공 주문(amount=0)이 있으면 "이용권에 포함" 안내 대상
          setHasPassIncludedPdfOrder(
            paidOrdersForProduct.some(
              (purchase: { amount?: number; isBundleComponent?: boolean }) => purchase.amount === 0 && !purchase.isBundleComponent
            )
          );
        }
      } catch (error) {
        console.error("구매 이력 확인 오류:", error);
        setPurchaseCheckFailed(true);
      } finally {
        setIsCheckingPurchase(false);
      }
    };

    checkPurchaseHistory();
  }, [isSignedIn, productId]);

  const handlePurchase = async () => {
    // 로그인 상태 확인
    if (!isLoaded) {
      return; // 로딩 중이면 대기
    }

    if (!isSignedIn) {
      // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
      router.push("/sign-in");
      return;
    }

    if (isLoading || (isToss && !tossReady)) return;

    if (!isToss && !selectedPaymentMethod) {
      alert("결제 수단을 선택해주세요.");
      return;
    }

    // JCG-3580: 보유 여부를 모르는 상태에서는 중복 결제가 될 수 있어 진행하지 않는다(방어적 가드)
    if (isCheckingPurchase || purchaseCheckFailed) {
      return;
    }

    // 로그인된 경우 결제 진행
    setIsLoading(true);

    try {
      const orderId = generateOrderId(orderIdPrefix);
      // PDF 가격은 구독 여부와 무관하며 서버에서도 동일 금액을 재검증한다.
      const amount = currentPrice;

      // 1. API를 통해 서버 사이드에서 임시 주문 정보 저장
      const createOrderResponse = await fetch("/api/create-pending-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId,
          goodsName,
          amount,
          buyerEmail: user?.primaryEmailAddress?.emailAddress
            || user?.emailAddresses?.[0]?.emailAddress
            || "",
          buyerName: user?.fullName || user?.firstName || "",
          productId,
          returnPageUrl: window.location.pathname,
          paymentMethod: isToss ? "widget" : selectedPaymentMethod,
        }),
      });

      if (!createOrderResponse.ok) {
        const errorData = await createOrderResponse.json();
        Sentry.captureMessage('Order creation failed', {
          level: 'error',
          tags: {
            component: 'PaymentSection',
            operation: 'create-order'
          },
          extra: {
            orderId,
            productId,
            goodsName,
            amount,
            error: errorData.error,
            userEmail: user?.primaryEmailAddress?.emailAddress
          }
        });
        throw new Error(errorData.error || "임시 주문 생성 실패");
      }

      const createdOrder = await createOrderResponse.json();
      if (createdOrder.paymentProvider === "toss") {
        if (!tossWidget.current) throw new Error("결제 수단을 다시 불러와주세요.");
        await tossWidget.current.requestPayment(createdOrder.checkout);
        return;
      }
      if (!isNicepayPaymentMethod(selectedPaymentMethod)) {
        throw new Error("결제 설정이 변경되었습니다. 페이지를 새로고침해주세요.");
      }

      // 2. Nicepay 결제 요청
      const returnUrl = `${process.env.NEXT_PUBLIC_APP_URL || window.location.origin}/api/serverAuth`;

      if (window.AUTHNICE) {
        const buyerEmail = user?.primaryEmailAddress?.emailAddress
          || user?.emailAddresses?.[0]?.emailAddress
          || "";
        const buyerName = user?.fullName || user?.firstName || "";

        window.AUTHNICE.requestPay({
          clientId: nicepayClientId,
          method: selectedPaymentMethod,
          orderId: orderId,
          amount: amount,
          goodsName,
          returnUrl,
          buyerName,
          buyerEmail,
          ...nicepayVbankOptions(selectedPaymentMethod),
          fnError: function (result: { msg: string; errorMsg: string }) {
            // 결제창 단계 실패는 서버·NicePay에 흔적이 없어 여기서만 관측된다(JCG-3604).
            Sentry.captureMessage("NicePay fnError", {
              level: "warning",
              tags: { component: "PaymentSection", operation: "requestPay" },
              extra: { orderId, productId: productId, method: selectedPaymentMethod, errorMsg: result.errorMsg, msg: result.msg },
            });
            alert("오류 : " + result.errorMsg);
            setPollingOrderId(null);
            setIsLoading(false);
          },
        });
        // 모바일에서 NicePay가 페이지를 이동시키므로 sessionStorage에 저장
        sessionStorage.setItem('pendingPaymentOrderId', orderId);
        // 결제 요청 후 polling 시작 (페이지가 유지될 경우를 대비)
        setPollingOrderId(orderId);
      } else {
        Sentry.captureMessage("NicePay SDK not loaded", {
          level: "warning",
          tags: { component: "PaymentSection", operation: "requestPay" },
          extra: { orderId, productId: productId, method: selectedPaymentMethod },
        });
        alert("결제 모듈이 로드되지 않았습니다.");
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      Sentry.captureException(error, {
        tags: {
          component: 'PaymentSection',
          operation: 'purchase'
        },
        extra: {
          productId,
          goodsName,
          selectedPaymentMethod,
          userEmail: user?.primaryEmailAddress?.emailAddress
        }
      });
      alert(`주문 처리 중 오류가 발생했습니다. 다시 시도해주세요.\n${errorMessage}`);
      setIsLoading(false);
    }
  };

  // 결제가: 유튜브 구독자 + 할인가 있으면 할인가, 아니면 정가
  const currentPrice = price;

  // 이용권에 포함된 PDF인 경우 별도 UI 표시
  if (isIncludedInPass) {
    return (
      <div>
        {/* 이용권 포함 안내 */}
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold text-blue-800 dark:text-blue-300">
              {passType === 'complete' ? 'Complete' : 'Intelligence'} 이용권 혜택
            </span>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            이 PDF는 현재 보유하신 이용권에 포함되어 있습니다.
          </p>
        </div>

        {/* 가격 표시 (취소선) */}
        <div className="mb-4">
          <div className="space-y-1">
            <p className="text-xl font-bold text-gray-400 line-through">
              {price.toLocaleString()}원
            </p>
            <p className="text-3xl font-bold text-green-600">
              무료
            </p>
            <p className="text-sm text-green-600 font-medium">
              이용권에 포함된 혜택
            </p>
          </div>
        </div>

        {/* 안내 박스 */}
        <div className="w-full bg-blue-600 text-white py-3 px-6 mb-2 rounded-lg font-semibold flex items-center justify-center gap-2">
          <Gift className="w-4 h-4" />
          이용권에 포함된 혜택
        </div>
        <div className="text-sm text-center text-gray-500">
          설정 {">"} 구매 목록에서 PDF를 다운로드할 수 있습니다
        </div>
      </div>
    );
  }

  // 이미 구매한 PDF인 경우 별도 UI 표시
  if (hasPurchased && !isCheckingPurchase) {
    return (
      <div>
        {/* 구매 완료 안내 */}
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingBag className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="font-semibold text-green-800 dark:text-green-300">
              구매 완료
            </span>
          </div>
          <p className="text-sm text-green-700 dark:text-green-300">
            이미 구매하신 상품입니다.
          </p>
        </div>

        {/* 가격 표시 */}
        <div className="mb-4">
          <div className="space-y-1">
            <p className="text-xl font-bold text-gray-400 line-through">
              {price.toLocaleString()}원
            </p>
            <p className="text-3xl font-bold text-green-600">
              구매 완료
            </p>
          </div>
        </div>

        {/* 안내 박스 */}
        <div className="w-full bg-green-600 text-white py-3 px-6 mb-2 rounded-lg font-semibold flex items-center justify-center gap-2">
          <Check className="w-4 h-4" />
          이미 구매한 상품
        </div>
        <div className="text-sm text-center text-gray-500">
          설정 {">"} 구매 목록에서 PDF를 다운로드할 수 있습니다
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* 결제 확인 중 오버레이 */}
      <PaymentPollingOverlay
        isVisible={!!pollingOrderId}
        onCancel={() => {
          setPollingOrderId(null);
          setIsLoading(false);
        }}
      />

      {/* 가격 표시 */}
      <div className="mb-4">
        <p className="text-3xl font-bold text-foreground/80">
          {currentPrice.toLocaleString()}원
        </p>
      </div>

      {/* 결제 수단 선택 */}
      {isToss ? (
        user ? (
          <TossPaymentWidget
            productId={productId}
            ref={tossWidget}
            amount={currentPrice}
            customerKey={user.id}
            onReadyChange={setTossReady}
          />
        ) : (
          <p className="text-sm text-muted-foreground">로그인 후 결제 수단을 선택할 수 있습니다.</p>
        )
      ) : (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">결제 수단</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {getPaymentMethods().map((method) => {
              const isSelected = selectedPaymentMethod === method.id;
              return (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setSelectedPaymentMethod(method.id)}
                  aria-pressed={isSelected}
                  className={`inline-flex min-h-11 touch-manipulation items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {isSelected && <Check className="w-3.5 h-3.5" aria-hidden />}
                  {method.label}
                </button>
              );
            })}
          </div>
          {selectedPaymentMethod === "vbank" && (
            <p
              className="mt-3 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm leading-relaxed text-muted-foreground"
              role="status"
            >
              24시간 이내 입금 · 입금 확인 후 이용 가능 · 현금영수증은 결제창에서 신청
            </p>
          )}
        </div>
      )}

      {/* 구매 버튼 */}
      <button
        onClick={handlePurchase}
        disabled={
          isLoading ||
          !isLoaded ||
          (isToss ? !!isSignedIn && !tossReady : !selectedPaymentMethod) ||
          isCheckingPurchase ||
          purchaseCheckFailed
        }
        className="w-full bg-primary/90 text-primary-foreground py-3 px-6 mb-2 rounded-lg font-semibold hover:bg-primary/80 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isCheckingPurchase
          ? "확인 중..."
          : purchaseCheckFailed
            ? "구매 정보를 확인할 수 없습니다"
            : isLoading
              ? "처리 중..."
              : "구매하기"}
      </button>
      {purchaseCheckFailed && (
        <p className="mb-2 text-sm text-center text-red-600 dark:text-red-400" role="alert">
          구매 이력을 불러오지 못했습니다. 중복 결제를 막기 위해 결제를 잠시 막아두었어요.
          페이지를 새로고침해 주세요.
        </p>
      )}
      {/* 결제 후 다운로드 가능 기간 */}
      <div className="text-sm text-center text-gray-500">
        결제 후 다운로드 가능 기간: 3개월
      </div>
    </div>
  );
}
